class SiteNav extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._mobileOpen = false;
    this._dropdownOpen = false;
  }

  static get observedAttributes() {
    return ['theme'];
  }

  get theme() {
    return this.getAttribute('theme') || 'dark';
  }

  connectedCallback() {
    this.render();
    this._bindEvents();
  }

  attributeChangedCallback() {
    this.render();
    this._bindEvents();
  }

  render() {
    const isDark = this.theme === 'dark';

    const textColor = isDark ? '#ffffff' : '#1a1a1a';
    const textColorMuted = isDark ? '#ffffff' : '#1a1a1a';
    const navBg = isDark ? 'rgba(0,0,0,1)' : 'rgba(255,255,255,1)';
    const borderBottom = isDark ? 'none' : '1px solid #e5e5e5';
    const logoFilter = isDark ? 'brightness(0) invert(1)' : 'none';
    const logoTextColor = isDark ? '#ffffff' : '#c8281e';
    const hamburgerColor = textColor;
    const dropdownBg = isDark ? 'rgba(20,20,30,0.95)' : '#ffffff';
    const dropdownBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e5e5';
    const dropdownTextColor = isDark ? 'rgba(255,255,255,0.85)' : '#333333';
    const dropdownHoverBg = isDark ? 'rgba(255,255,255,0.08)' : '#f5f5f5';
    const mobileBg = isDark ? 'rgba(10,10,20,0.97)' : '#ffffff';
    const mobileBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e5e5';
    const hoverColor = '#c8281e';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: sticky;
          top: 0;
          z-index: 1000;
          font-family: -apple-system, "SF Pro Text", "SF Pro Display",
                       BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }

        .navbar {
          width: 100%;
          z-index: 999999999;
          background: ${navBg};
          border-bottom: ${borderBottom};
          transition: background 0.3s, border 0.3s;
        }

        @media (max-width: 767px) {
          .navbar {
            background: ${isDark ? 'rgba(0,0,0,0.95)' : 'rgba(255,255,255,0.95)'};
          }
        }

        .nav-inner {
          display: flex;
          z-index: 999999999;
          align-items: center;
          justify-content: space-between;
          height: 72px;
          padding: 0 16px;
        }

        @media (min-width: 768px) {
          .nav-inner {
            padding: 0 60px;
          }
        }

        .nav-right {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        /* ── Logo ── */
        .logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          flex-shrink: 0;
        }

        .logo img {
          height: 40px;
          width: auto;
          object-fit: contain;
          
        }

        .logo-text {
          font-weight: 800;
          font-size: 18px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: ${logoTextColor};
          line-height: 1;
        }

        /* ── Desktop nav ── */
        .nav-links {
          display: none;
          align-items: center;
          gap: 36px;
          list-style: none;
        }

        @media (min-width: 768px) {
          .nav-links { display: flex; }
        }

        .nav-links a {
          text-decoration: none;
          font-size: 16px;
          font-weight: 500;
          color: ${textColorMuted};
          transition: color 0.2s;
          white-space: nowrap;
        }

        .nav-links a:hover {
          color: ${hoverColor};
        }

        /* ── Dropdown ── */
        .dropdown {
          position: relative;
        }

        .dropdown-trigger {
          display: flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
          color: ${textColorMuted};
          transition: color 0.2s;
          background: none;
          border: none;
          font-family: inherit;
          padding: 0;
        }

        .dropdown-trigger:hover {
          color: ${hoverColor};
        }

        .dropdown-trigger svg {
          width: 12px;
          height: 12px;
          fill: currentColor;
          transition: transform 0.2s;
        }

        .dropdown-trigger.open svg {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          display: none;
          position: absolute;
          top: calc(100% + 14px);
          left: -16px;
          min-width: 300px;
          background: ${dropdownBg};
          border: ${dropdownBorder};
          border-radius: 12px;
          padding: 8px 0;
          box-shadow: 0 12px 40px rgba(0,0,0,0.18);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          z-index: 100;
        }

        /* Invisible bridge to keep hover active between trigger and menu */
        .dropdown-menu::before {
          content: '';
          position: absolute;
          top: -20px;
          left: 0;
          width: 100%;
          height: 20px;
        }

        .dropdown-menu.show {
          display: block;
          animation: fadeIn 0.18s ease;
        }

        .dropdown-menu a {
          display: block;
          padding: 10px 20px;
          font-size: 16px;
          font-weight: 500;
          color: ${dropdownTextColor};
          text-decoration: none;
          transition: background 0.15s, color 0.15s;
          border-radius: 0;
        }

        .dropdown-menu a:hover {
          background: ${dropdownHoverBg};
          color: ${hoverColor};
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ── Hamburger ── */
        .hamburger {
          display: flex;
          flex-direction: column;
          gap: 5px;
          cursor: pointer;
          padding: 4px;
          background: none;
          border: none;
        }

        @media (min-width: 768px) {
          .hamburger { display: none; }
        }

        .hamburger span {
          display: block;
          width: 22px;
          height: 2px;
          background: ${hamburgerColor};
          border-radius: 2px;
          transition: transform 0.3s, opacity 0.3s;
        }

        .hamburger.open span:nth-child(1) {
          transform: rotate(45deg) translate(5px, 5px);
        }
        .hamburger.open span:nth-child(2) {
          opacity: 0;
        }
        .hamburger.open span:nth-child(3) {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        /* ── Mobile menu ── */
        .mobile-menu {
          display: none;
          flex-direction: column;
          background: ${mobileBg};
          border-top: ${mobileBorder};
          padding: 16px 20px 24px;
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }

        @media (min-width: 768px) {
          .mobile-menu { display: none !important; }
        }

        .mobile-menu.open {
          display: flex;
        }

        .mobile-menu a {
          display: block;
          padding: 12px 0;
          font-size: 15px;
          font-weight: 500;
          color: ${textColorMuted};
          text-decoration: none;
          border-bottom: ${mobileBorder};
          transition: color 0.2s;
        }

        .mobile-menu a:last-child {
          border-bottom: none;
        }

        .mobile-menu a:hover {
          color: ${hoverColor};
        }

        .mobile-dropdown-label {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 0;
          font-size: 15px;
          font-weight: 500;
          color: ${textColorMuted};
          cursor: pointer;
          border-bottom: ${mobileBorder};
          background: none;
          border-left: none;
          border-right: none;
          border-top: none;
          font-family: inherit;
          width: 100%;
          text-align: left;
        }

        .mobile-dropdown-label svg {
          width: 14px;
          height: 14px;
          fill: currentColor;
          transition: transform 0.2s;
        }

        .mobile-dropdown-label.open svg {
          transform: rotate(180deg);
        }

        .mobile-submenu {
          display: none;
          flex-direction: column;
          padding-left: 16px;
        }

        .mobile-submenu.open {
          display: flex;
        }

        .mobile-submenu a {
          font-size: 14px;
          padding: 10px 0;
          color: ${dropdownTextColor};
        }

        /* ── CTA Button ── */
        .cta-btn {
          display: none;
          padding: 10px 22px;
          background: #c8281e;
          color: #ffffff;
          font-size: 16px;
          font-weight: 500;
          border-radius: 8px;
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.2s;
          margin-left: 8px;
        }

        .cta-btn:hover {
          opacity: 0.88;
        }

        @media (min-width: 768px) {
          .cta-btn { display: inline-block; }
        }

        .mobile-cta {
          display: block;
          text-align: center;
          padding: 14px 0;
          margin-top: 8px;
          background: #c8281e;
          color: #ffffff;
          font-size: 15px;
          font-weight: 600;
          border-radius: 8px;
          text-decoration: none;
          transition: opacity 0.2s;
        }

        .mobile-cta:hover {
          opacity: 0.88;
        }
      </style>

      <header class="navbar">
        <div class="nav-inner">
          <a class="logo" href="index.html">
            <img loading="lazy" decoding="async" src="${new URL('./images/logo.png', import.meta.url).href}" alt="FUE Global" />
          </a>

          <div class="nav-right">
          <ul class="nav-links">
            <li class="dropdown">
              <button class="dropdown-trigger" id="about-trigger" aria-expanded="false" aria-haspopup="true">
                About
                <svg viewBox="0 0 12 8"><path d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z"/></svg>
              </button>
              <div class="dropdown-menu" id="about-menu" role="menu">
                <a href="about.html" role="menuitem">Who We Are</a>
                <a href="about.html#vision-mission" role="menuitem">Mission and Vision</a>
                <a href="about.html#scientific-board" role="menuitem">FUE Global Scientific Board</a>
                <a href="GHTP.html" role="menuitem">Global Hair Transplant Board</a>
                <a href="outreach-program.html" role="menuitem">Outreach Program</a>
                <a href="operation-hope.html" role="menuitem">Operation Hope</a>
                <a href="event.html#past-events" role="menuitem">Past Conferences</a>
              </div>
            </li>
            <li class="dropdown">
              <button class="dropdown-trigger" id="dropdown-trigger" aria-expanded="false" aria-haspopup="true">
                Event
                <svg viewBox="0 0 12 8"><path d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z"/></svg>
              </button>
              <div class="dropdown-menu" id="dropdown-menu" role="menu">
                <a href="event.html" role="menuitem">HMSC – 3rd FUE Global World Congress</a>
                <a href="event.html#hotel-accommodation" data-scroll-target="hotel-accommodation" role="menuitem">Event Venue</a>
                <a href="visa-assistance.html" role="menuitem">Visa Assistance</a>
              </div>
            </li>
            <li class="dropdown">
              <button class="dropdown-trigger" id="journal-trigger" aria-expanded="false" aria-haspopup="true">
                Journal
                <svg viewBox="0 0 12 8"><path d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z"/></svg>
              </button>
              <div class="dropdown-menu" id="journal-menu" role="menu">
                <a href="journal-JHRRM.html" role="menuitem">Journal JHRRM</a>
              </div>
            </li>
            <li><a href="contact.html">Contact us</a></li>
          </ul>

          <a href="registration-page.html" class="cta-btn">Register Now</a>
          </div>

          <button class="hamburger" id="hamburger" aria-label="Toggle menu">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        <div class="mobile-menu" id="mobile-menu">
          <button class="mobile-dropdown-label" id="mobile-about-toggle">
            About
            <svg viewBox="0 0 12 8"><path d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z"/></svg>
          </button>
          <div class="mobile-submenu" id="mobile-about-submenu">
            <a href="about.html">Who We Are</a>
            <a href="about.html#vision-mission">Mission and Vision</a>
            <a href="about.html#scientific-board">FUE Global Scientific Board</a>
            <a href="GHTP.html">Global Hair Transplant Board</a>
            <a href="outreach-program.html">Outreach Program</a>
            <a href="operation-hope.html">Operation Hope</a>
            <a href="event.html#past-events">Past Conferences</a>
          </div>
          <button class="mobile-dropdown-label" id="mobile-event-toggle">
            Event
            <svg viewBox="0 0 12 8"><path d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z"/></svg>
          </button>
          <div class="mobile-submenu" id="mobile-submenu">
            <a href="event.html">HMSC – 3rd FUE Global World Congress</a>
            <a href="event.html#hotel-accommodation" data-scroll-target="hotel-accommodation">Event Venue</a>
            <a href="visa-assistance.html">Visa Assistance</a>
          </div>
          <button class="mobile-dropdown-label" id="mobile-journal-toggle">
            Journal
            <svg viewBox="0 0 12 8"><path d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z"/></svg>
          </button>
          <div class="mobile-submenu" id="mobile-journal-submenu">
            <a href="journal-JHRRM.html">Journal JHRRM</a>
          </div>
          <a href="contact.html">Contact us</a>
          <a href="registration-page.html" class="mobile-cta">Register Now</a>
        </div>
      </header>
    `;
  }

  _bindEvents() {
    const shadow = this.shadowRoot;

    // Helper to setup a desktop dropdown
    const setupDropdown = (triggerId, menuId) => {
      const trigger = shadow.getElementById(triggerId);
      const menu = shadow.getElementById(menuId);
      const dropdown = trigger?.closest('.dropdown');
      if (!dropdown) return;

      dropdown.addEventListener('mouseenter', () => {
        menu.classList.add('show');
        trigger.classList.add('open');
        trigger.setAttribute('aria-expanded', 'true');
      });

      dropdown.addEventListener('mouseleave', () => {
        menu.classList.remove('show');
        trigger.classList.remove('open');
        trigger.setAttribute('aria-expanded', 'false');
      });

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = menu.classList.contains('show');
        menu.classList.toggle('show', !isOpen);
        trigger.classList.toggle('open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });

      // Close on outside click
      document.addEventListener('click', () => {
        menu.classList.remove('show');
        trigger.classList.remove('open');
        trigger.setAttribute('aria-expanded', 'false');
      });
    };

    // Desktop dropdowns
    setupDropdown('about-trigger', 'about-menu');
    setupDropdown('dropdown-trigger', 'dropdown-menu');
    setupDropdown('journal-trigger', 'journal-menu');

    shadow.querySelectorAll('[data-scroll-target]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const targetId = link.getAttribute('data-scroll-target');
        const target = targetId ? document.getElementById(targetId) : null;
        const isEventPage = /(^|\/)event\.html$/.test(window.location.pathname) || window.location.pathname.endsWith('/');

        if (!target || !isEventPage) return;

        event.preventDefault();
        shadow.querySelectorAll('.dropdown-menu.show').forEach(menu => menu.classList.remove('show'));
        shadow.querySelectorAll('.dropdown-trigger.open').forEach(trigger => {
          trigger.classList.remove('open');
          trigger.setAttribute('aria-expanded', 'false');
        });

        this._mobileOpen = false;
        mobileMenu?.classList.remove('open');
        hamburger?.classList.remove('open');

        if (window.__lenisInstance?.scrollTo) {
          window.__lenisInstance.scrollTo(target, { offset: -72 });
        } else {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        history.replaceState(null, '', `#${targetId}`);
      });
    });

    // Mobile hamburger
    const hamburger = shadow.getElementById('hamburger');
    const mobileMenu = shadow.getElementById('mobile-menu');

    hamburger?.addEventListener('click', () => {
      this._mobileOpen = !this._mobileOpen;
      mobileMenu.classList.toggle('open', this._mobileOpen);
      hamburger.classList.toggle('open', this._mobileOpen);
    });

    // Helper for mobile dropdowns
    const setupMobileDropdown = (toggleId, submenuId, stateKey) => {
      const toggle = shadow.getElementById(toggleId);
      const submenu = shadow.getElementById(submenuId);
      toggle?.addEventListener('click', () => {
        this[stateKey] = !this[stateKey];
        submenu.classList.toggle('open', this[stateKey]);
        toggle.classList.toggle('open', this[stateKey]);
      });
    };

    setupMobileDropdown('mobile-about-toggle', 'mobile-about-submenu', '_aboutDropdownOpen');
    setupMobileDropdown('mobile-event-toggle', 'mobile-submenu', '_dropdownOpen');
    setupMobileDropdown('mobile-journal-toggle', 'mobile-journal-submenu', '_journalDropdownOpen');
  }
}

customElements.define('site-nav', SiteNav);

// ── Lenis Smooth Scroll (auto-loaded on every page) ──
(function initLenis() {
  // Don't double-init if already loaded
  if (window.__lenisInstance) return;

  const script = document.createElement('script');
  script.src = 'https://unpkg.com/lenis@1/dist/lenis.min.js';
  script.onload = () => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      touchMultiplier: 2,
      infinite: false,
    });

    window.__lenisInstance = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);
  };

  document.head.appendChild(script);
})();
